/**
 * Mouse tracking system for capturing cursor movements during recording
 * Captures at 60fps for smooth playback
 */

import type { MouseEvent as TrackedMouseEvent, CursorShape } from '@/store/recording-store'

class MouseTracker {
    private events: TrackedMouseEvent[] = []
    private isTracking = false
    private startTime = 0
    private lastX = 0
    private lastY = 0
    private lastMoveTime = 0
    private dwellTimeout: number | null = null
    private frameId: number | null = null
    private pendingEvents: { x: number; y: number }[] = []
    
    private screenWidth = 1920
    private screenHeight = 1080
    private currentShape: CursorShape = 'arrow'

    private readonly DWELL_THRESHOLD = 400 // ms
    private readonly SAMPLE_INTERVAL = 16 // ~60fps (1000/60)
    private readonly MIN_MOVE_DISTANCE = 2 // pixels

    /**
     * Start tracking mouse movements
     */
    start(screenWidth: number = 1920, screenHeight: number = 1080): void {
        if (this.isTracking) return

        this.screenWidth = screenWidth
        this.screenHeight = screenHeight

        this.events = []
        this.pendingEvents = []
        this.isTracking = true
        this.startTime = Date.now()
        this.lastMoveTime = 0
        this.currentShape = 'arrow'

        // Add event listeners
        document.addEventListener('pointermove', this.handlePointerMove)
        document.addEventListener('pointerdown', this.handlePointerDown)
        document.addEventListener('click', this.handleClick)

        // Start the sampling loop
        this.startSampling()
    }

    /**
     * Stop tracking and return captured events
     */
    stop(): TrackedMouseEvent[] {
        if (!this.isTracking) return this.events

        this.isTracking = false

        // Remove event listeners
        document.removeEventListener('pointermove', this.handlePointerMove)
        document.removeEventListener('pointerdown', this.handlePointerDown)
        document.removeEventListener('click', this.handleClick)

        // Stop sampling
        if (this.frameId) {
            cancelAnimationFrame(this.frameId)
            this.frameId = null
        }

        // Clear dwell timeout
        if (this.dwellTimeout) {
            clearTimeout(this.dwellTimeout)
            this.dwellTimeout = null
        }

        return this.events
    }

    /**
     * Get current events (while recording)
     */
    getEvents(): TrackedMouseEvent[] {
        return [...this.events]
    }
    
    private getCursorShape(element: Element | null): CursorShape {
        if (!element) return 'arrow'
        const cursor = window.getComputedStyle(element).cursor
        if (cursor === 'pointer') return 'hand'
        if (cursor === 'text') return 'ibeam'
        if (cursor === 'wait' || cursor === 'progress') return 'wait'
        if (cursor === 'crosshair') return 'crosshair'
        if (cursor.includes('resize')) return 'resize'
        return 'arrow'
    }

    /**
     * Handle pointer move events
     */
    private handlePointerMove = (e: PointerEvent): void => {
        // Detect shape
        const shape = this.getCursorShape(e.target as Element)
        if (shape !== this.currentShape) {
            this.currentShape = shape
            const scaledX = e.clientX * (this.screenWidth / window.innerWidth)
            const scaledY = e.clientY * (this.screenHeight / window.innerHeight)
            this.addEvent(scaledX, scaledY, 'shape', shape)
        }

        const events = e.getCoalescedEvents ? e.getCoalescedEvents() : [e]
        
        for (const ev of events) {
            const scaledX = ev.clientX * (this.screenWidth / window.innerWidth)
            const scaledY = ev.clientY * (this.screenHeight / window.innerHeight)
            this.pendingEvents.push({ x: scaledX, y: scaledY })
        }

        // Reset dwell detection
        if (this.dwellTimeout) {
            clearTimeout(this.dwellTimeout)
        }

        // Start dwell detection using the last event position
        if (events.length > 0) {
            const lastEv = events[events.length - 1]
            const finalX = lastEv.clientX * (this.screenWidth / window.innerWidth)
            const finalY = lastEv.clientY * (this.screenHeight / window.innerHeight)
            
            this.dwellTimeout = window.setTimeout(() => {
                if (this.isTracking) {
                    this.addEvent(finalX, finalY, 'dwell')
                }
            }, this.DWELL_THRESHOLD)
        }
    }

    /**
     * Handle pointer down events
     */
    private handlePointerDown = (e: PointerEvent): void => {
        if (!this.isTracking) return
        const scaledX = e.clientX * (this.screenWidth / window.innerWidth)
        const scaledY = e.clientY * (this.screenHeight / window.innerHeight)
        this.addEvent(scaledX, scaledY, 'click')
    }

    /**
     * Handle click events
     */
    private handleClick = (e: MouseEvent): void => {
        if (!this.isTracking) return
        const scaledX = e.clientX * (this.screenWidth / window.innerWidth)
        const scaledY = e.clientY * (this.screenHeight / window.innerHeight)
        this.addEvent(scaledX, scaledY, 'click')
    }

    /**
     * Start the 60fps sampling loop
     */
    private startSampling(): void {
        const sample = () => {
            if (!this.isTracking) return

            const now = Date.now()

            // Only sample at intervals
            if (now - this.lastMoveTime >= this.SAMPLE_INTERVAL && this.pendingEvents.length > 0) {
                
                for (const point of this.pendingEvents) {
                    const { x, y } = point
                    // Only record if moved enough
                    const distance = Math.sqrt(
                        Math.pow(x - this.lastX, 2) + Math.pow(y - this.lastY, 2)
                    )

                    if (distance >= this.MIN_MOVE_DISTANCE) {
                        this.addEvent(x, y, 'move')
                        this.lastX = x
                        this.lastY = y
                    }
                }
                
                this.pendingEvents = [] // Clear processed events
                this.lastMoveTime = now
            }

            this.frameId = requestAnimationFrame(sample)
        }

        this.frameId = requestAnimationFrame(sample)
    }

    /**
     * Add an event to the list
     */
    private addEvent(x: number, y: number, type: TrackedMouseEvent['type'], shape?: CursorShape): void {
        const t = Date.now() - this.startTime
        if (type === 'shape') {
            this.events.push({ t, x, y, type, shape })
        } else {
            this.events.push({ t, x, y, type })
        }
    }
}

// Singleton instance
export const mouseTracker = new MouseTracker()
